import { useEffect, useMemo, useRef, useState } from "react";
import {
  PLAYBACK_BAR_COUNT,
  downsampleWaveform,
  extractPeaksFromUrl,
} from "../utils/audioWaveform";

export default function PlaybackWaveform({
  audioUrl,
  currentTime,
  duration,
  bars: barsProp,
  onSeekRatio,
  onDecodedMeta,
}) {
  const stored = useMemo(
    () => (Array.isArray(barsProp) && barsProp.length ? downsampleWaveform(barsProp, PLAYBACK_BAR_COUNT) : null),
    [barsProp]
  );
  const [fileBars, setFileBars] = useState(null);
  const onDecodedMetaRef = useRef(onDecodedMeta);
  onDecodedMetaRef.current = onDecodedMeta;
  const bars = fileBars ?? stored ?? Array.from({ length: PLAYBACK_BAR_COUNT }, () => 0.22);
  const progress = duration > 0 ? Math.min(1, Math.max(0, currentTime / duration)) : 0;
  const playedCount = Math.round(progress * bars.length);

  useEffect(() => {
    if (!audioUrl) return undefined;
    const canDecode =
      audioUrl.startsWith("blob:") ||
      (() => {
        try {
          return new URL(audioUrl, window.location.href).origin === window.location.origin;
        } catch {
          return false;
        }
      })();
    if (!canDecode) return undefined;
    let cancelled = false;
    setFileBars(null);
    void extractPeaksFromUrl(audioUrl).then((meta) => {
      if (cancelled || !meta?.waveform?.length) return;
      setFileBars(meta.waveform);
      if (meta.durationMs > 0) {
        onDecodedMetaRef.current?.({ durationSec: meta.durationMs / 1000 });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [audioUrl]);

  const seekFromEvent = (clientX, target) => {
    const rect = target.getBoundingClientRect();
    if (!rect.width) return;
    onSeekRatio(Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)));
  };

  return (
    <button
      type="button"
      className="flex h-10 min-w-[8.5rem] flex-1 items-center overflow-hidden rounded-md bg-transparent"
      aria-label="Seek voice message"
      onClick={(e) => seekFromEvent(e.clientX, e.currentTarget)}
    >
      {bars.map((level, index) => {
        const played = index < playedCount;
        return (
          <span
            key={index}
            className="mx-px block min-w-[2px] flex-1 rounded-full"
            style={{
              height: `${6 + level * 28}px`,
              backgroundColor: played ? "#ffffff" : "rgba(255,255,255,0.38)",
            }}
          />
        );
      })}
    </button>
  );
}
